import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { RefreshCw, Eye } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { reserveUrl } from '@/lib/apiUrls';
import { mergeReserveActAsHeaders, mergeSupabaseEdgeHeaders, getReserveActAsUserId, setReserveActAsUserId } from '@/lib/reserveActAs';
import { User } from '@/api/entities';
import { stripePromise } from '@/lib/stripe';
import { RESERVE_TERMS_VERSION } from './ReserveTerms';
import { useReserveEligibility } from '@/hooks/useReserveEligibility';

import {
  ReserveBalanceCard,
  PaymentMethodsManager,
  ReserveLedgerTable,
  AddFundsModal,
  AddCardModal,
  ReserveTermsModal,
  ReserveTripsCard,
  ReserveFAQ,
  AdminAllTripsModal,
  ReserveLedgerRepairModal,
} from '@/components/reserve';

export default function ReserveDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  const [balance, setBalance] = useState({ balance_cents: 0, updated_at: null });
  const [isLoadingBalance, setIsLoadingBalance] = useState(true);
  
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(true);
  /** False when server has no STRIPE_SECRET_KEY (same empty UI as "no cards" before — now we explain). */
  const [stripePaymentsAvailable, setStripePaymentsAvailable] = useState(true);
  /** True when Stripe customer exists in DB but list/retrieve failed (e.g. live/test mismatch). */
  const [paymentMethodsError, setPaymentMethodsError] = useState(false);
  /** Server-provided Stripe error snapshot (type, code, message) when load fails. */
  const [stripeLoadError, setStripeLoadError] = useState(null);
  
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [isLoadingLedger, setIsLoadingLedger] = useState(true);

  const [reserveTrips, setReserveTrips] = useState([]);
  const [isLoadingTrips, setIsLoadingTrips] = useState(true);
  
  const [showAddFundsModal, setShowAddFundsModal] = useState(false);
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showAllTripsModal, setShowAllTripsModal] = useState(false);
  const [showLedgerRepairModal, setShowLedgerRepairModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [isCheckingTerms, setIsCheckingTerms] = useState(true);
  const [pendingAction, setPendingAction] = useState(null);
  const [actAsUserId, setActAsUserIdState] = useState(() => getReserveActAsUserId());

  // When the dashboard is opened with a deep link from a checkout that needs top-up
  // (e.g. /app/reserve?topup_for_cents=49628&topup_for_label=Kingston%20%E2%86%92%20Houston&return_to=/Review),
  // we pre-configure the Add Funds modal with the exact shortfall and remember where to
  // send the user back to after a successful top-up. Params are stripped from the URL
  // immediately so a refresh doesn't keep re-opening the modal.
  const [topupSuggestion, setTopupSuggestion] = useState(null);

  const isAdmin = Boolean(
    user?.app_metadata?.roles?.includes('admin') || user?.email === 'jayjay.gonzalez@icloud.com'
  );

  // Defera Reserve was discontinued for new users. Only existing customers
  // with a wallet balance (or funds allocated to an active Reserve trip)
  // keep access to the dashboard. Admins always pass — they need to be able
  // to act-as customers for support, and the eligibility hook already keys
  // off the act-as id so admin-on-balance views still resolve correctly.
  const {
    loading: isReserveEligibilityLoading,
    isEligible: isReserveEligible,
  } = useReserveEligibility();

  useEffect(() => {
    window.scrollTo(0, 0);
    loadUser();
  }, []);

  useEffect(() => {
    if (isReserveEligibilityLoading || !user) return;
    if (isAdmin) return;
    if (!isReserveEligible) {
      navigate('/', { replace: true });
    }
  }, [isReserveEligibilityLoading, isReserveEligible, isAdmin, user, navigate]);

  useEffect(() => {
    const uid = location.state?.reserveActAsUserId;
    if (!uid || typeof uid !== 'string') return;
    setReserveActAsUserId(uid);
    setActAsUserIdState(uid);
    navigate(location.pathname, { replace: true, state: {} });
    toast.success('Viewing this customer\'s Reserve');
  }, [location.state?.reserveActAsUserId, location.pathname, navigate]);

  // Handle deep-link top-ups from the Review page. We only allow same-origin paths in
  // `return_to` so a malicious link can't bounce the user to a phishing site after they
  // pay. The terms gate still runs — if the user hasn't accepted Reserve terms yet, they
  // see the terms modal first and the suggestion stays queued until they accept.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cents = parseInt(params.get('topup_for_cents') || '', 10);
    if (!Number.isFinite(cents) || cents <= 0) return;
    const rawReturnTo = params.get('return_to') || '';
    const returnTo = rawReturnTo.startsWith('/') && !rawReturnTo.startsWith('//')
      ? rawReturnTo
      : null;
    const label = (params.get('topup_for_label') || '').slice(0, 80) || null;
    setTopupSuggestion({ amountCents: cents, label, returnTo });
    // Strip the params so a page refresh doesn't replay this. Preserve any existing
    // pathname/state so we don't disturb other deep-link handling.
    navigate(location.pathname, { replace: true, state: location.state ?? {} });
  }, [location.search, location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!topupSuggestion) return;
    if (showAddFundsModal || showTermsModal) return;
    if (isCheckingTerms) return;
    if (!hasAcceptedTerms) {
      setPendingAction('addFunds');
      setShowTermsModal(true);
      return;
    }
    setShowAddFundsModal(true);
  }, [topupSuggestion, hasAcceptedTerms, isCheckingTerms, showAddFundsModal, showTermsModal]);

  const loadUser = async () => {
    try {
      const u = await User.getCurrentUser();
      if (!u) {
        navigate('/home?auth_mode=login');
        return;
      }
      setUser(u);
      setIsLoading(false);
    } catch (error) {
      console.error('Failed to load user:', error);
      navigate('/home?auth_mode=login');
    }
  };

  const fetchPaymentMethods = useCallback(async () => {
    if (!user) return;
    setIsLoadingPaymentMethods(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const response = await fetch(reserveUrl('payment-methods'), {
        headers: mergeSupabaseEdgeHeaders({ Authorization: `Bearer ${session.access_token}` }),
      });
      if (response.ok) {
        const data = await response.json();
        setPaymentMethods(data.payment_methods || []);
        setDefaultPaymentMethod(data.default_payment_method);
        if (typeof data.stripe_payments_available === 'boolean') {
          setStripePaymentsAvailable(data.stripe_payments_available);
        }
        if (typeof data.payment_methods_error === 'boolean') {
          setPaymentMethodsError(data.payment_methods_error);
        } else {
          setPaymentMethodsError(false);
        }
        setStripeLoadError(data.stripe_error || null);
      } else {
        const errText = await response.text().catch(() => '');
        if (response.status === 503) {
          try {
            const j = JSON.parse(errText);
            if (j.stripe_payments_available === false) setStripePaymentsAvailable(false);
          } catch {
            /* ignore */
          }
        }
        console.warn('Reserve payment-methods request failed', {
          status: response.status,
          body: errText?.slice(0, 500),
        });
      }
    } catch (error) {
      console.error('Failed to fetch payment methods:', error);
    } finally {
      setIsLoadingPaymentMethods(false);
    }
  }, [user, actAsUserId]);

  // Balance, ledger, trips from Vercel; saved cards load separately via Supabase Edge (see fetchPaymentMethods).
  const fetchDashboard = useCallback(async () => {
    if (!user) return;

    setIsLoadingBalance(true);
    setIsLoadingLedger(true);
    setIsLoadingTrips(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setIsLoadingPaymentMethods(false);
        return;
      }

      const response = await fetch(reserveUrl('dashboard') + '?ledger_limit=50', {
        headers: mergeReserveActAsHeaders({ 'Authorization': `Bearer ${session.access_token}` })
      });

      if (response.status === 429) {
        toast.error('Too many requests. Please wait a moment and try again.');
        setIsLoadingPaymentMethods(false);
        return;
      }
      if (response.ok) {
        const data = await response.json();
        const bal = data.balance || {};
        setBalance({
          balance_cents: bal.balance_cents || 0,
          total_allocated_cents: bal.total_allocated_cents ?? 0,
          allocation_column_missing: bal.allocation_column_missing ?? false,
          updated_at: bal.updated_at
        });
        setLedgerEntries((data.ledger?.entries) || []);
        setReserveTrips(data.trips || []);
        await fetchPaymentMethods();
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.error || `Reserve dashboard failed (${response.status})`);
        setIsLoadingPaymentMethods(false);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      setIsLoadingPaymentMethods(false);
    } finally {
      setIsLoadingBalance(false);
      setIsLoadingLedger(false);
      setIsLoadingTrips(false);
    }
  }, [user, actAsUserId, fetchPaymentMethods]);

  const fetchBalance = useCallback(async () => {
    if (!user) return;
    setIsLoadingBalance(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const response = await fetch(reserveUrl('balance'), {
        headers: mergeReserveActAsHeaders({ 'Authorization': `Bearer ${session.access_token}` })
      });
      if (response.ok) {
        const data = await response.json();
        setBalance({
          balance_cents: data.balance_cents || 0,
          total_allocated_cents: data.total_allocated_cents ?? 0,
          allocation_column_missing: data.allocation_column_missing ?? false,
          updated_at: data.updated_at
        });
      }
    } catch (error) {
      console.error('Failed to fetch balance:', error);
    } finally {
      setIsLoadingBalance(false);
    }
  }, [user, actAsUserId]);

  const fetchLedger = useCallback(async () => {
    if (!user) return;
    setIsLoadingLedger(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const response = await fetch(reserveUrl('ledger') + '?limit=50', {
        headers: mergeReserveActAsHeaders({ 'Authorization': `Bearer ${session.access_token}` })
      });
      if (response.ok) {
        const data = await response.json();
        setLedgerEntries(data.entries || []);
      }
    } catch (error) {
      console.error('Failed to fetch ledger:', error);
    } finally {
      setIsLoadingLedger(false);
    }
  }, [user, actAsUserId]);

  const fetchTrips = useCallback(async () => {
    if (!user) return;
    setIsLoadingTrips(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const response = await fetch(reserveUrl('trips'), {
        headers: mergeReserveActAsHeaders({ 'Authorization': `Bearer ${session.access_token}` })
      });
      if (response.ok) {
        const data = await response.json();
        setReserveTrips(data.trips || []);
      }
    } catch (error) {
      console.error('Failed to fetch reserve trips:', error);
    } finally {
      setIsLoadingTrips(false);
    }
  }, [user, actAsUserId]);

  const checkTermsAcceptance = useCallback(async () => {
    if (!user) return;

    if (getReserveActAsUserId()) {
      setHasAcceptedTerms(true);
      setIsCheckingTerms(false);
      return;
    }
    
    setIsCheckingTerms(true);
    try {
      // Use session user ID for consistency
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setHasAcceptedTerms(false);
        return;
      }

      const { data, error } = await supabase
        .from('reserve_terms_acceptance')
        .select('version')
        .eq('user_id', session.user.id)
        .eq('version', RESERVE_TERMS_VERSION)
        .maybeSingle();
      
      // PGRST116 is "no rows returned" which is expected for new users
      if (error && error.code !== 'PGRST116') {
        console.error('Error checking terms acceptance:', error);
      }
      
      setHasAcceptedTerms(!!data);
    } catch (error) {
      console.error('Exception checking terms acceptance:', error);
      setHasAcceptedTerms(false);
    } finally {
      setIsCheckingTerms(false);
    }
  }, [user, actAsUserId]);

  useEffect(() => {
    if (user) {
      fetchDashboard();
      checkTermsAcceptance();
    }
  }, [user, actAsUserId, fetchDashboard, checkTermsAcceptance]);

  const handleRefreshAll = async () => {
    const clearingCustomerView = isAdmin && Boolean(getReserveActAsUserId());
    if (clearingCustomerView) {
      setReserveActAsUserId('');
      setActAsUserIdState('');
    }
    await fetchDashboard();
    await checkTermsAcceptance();
    if (clearingCustomerView) {
      toast.success('Customer view cleared');
    } else {
      toast.success('Data refreshed');
    }
  };

  const handleSetDefaultCard = async (paymentMethodId) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Please sign in');

    const response = await fetch(reserveUrl('set-default-payment-method'), {
      method: 'POST',
      headers: mergeSupabaseEdgeHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      }),
      body: JSON.stringify({ payment_method_id: paymentMethodId })
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to set default card');
    }

    await fetchPaymentMethods();
  };

  const handleRemoveCard = async (paymentMethodId) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Please sign in');

    const response = await fetch(reserveUrl('detach-payment-method'), {
      method: 'POST',
      headers: mergeSupabaseEdgeHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      }),
      body: JSON.stringify({ payment_method_id: paymentMethodId })
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to remove card');
    }

    await fetchPaymentMethods();
  };

  const handlePaymentSuccess = async () => {
    await Promise.all([
      fetchBalance(),
      fetchLedger()
    ]);
  };

  const handleCardAdded = async () => {
    await fetchPaymentMethods();
  };

  const handleAddFundsClick = () => {
    if (!hasAcceptedTerms) {
      setPendingAction('addFunds');
      setShowTermsModal(true);
    } else {
      setShowAddFundsModal(true);
    }
  };

  const handleAddCardClick = () => {
    if (!hasAcceptedTerms) {
      setPendingAction('addCard');
      setShowTermsModal(true);
    } else {
      setShowAddCardModal(true);
    }
  };

  const handleTermsAccepted = async (acceptance) => {
    try {
      // Get fresh session to ensure we have valid auth
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.error('No valid session for terms acceptance:', sessionError);
        toast.error('Please sign in again to continue.');
        return;
      }

      const userId = session.user.id;
      console.log('Recording terms acceptance for user:', userId);

      const { error } = await supabase
        .from('reserve_terms_acceptance')
        .insert({
          user_id: userId,
          version: acceptance.version,
          accepted_at: acceptance.acceptedAt
        });

      if (error) {
        console.error('Failed to record terms acceptance:', error);
        
        // Check for specific error types
        if (error.code === '23505') {
          // Unique constraint violation - already accepted
          setHasAcceptedTerms(true);
          setShowTermsModal(false);
          toast.success('Terms already accepted');
        } else if (error.code === '42501' || error.message?.includes('policy')) {
          toast.error('Permission error. Please refresh and try again.');
        } else {
          toast.error(`Failed to save terms: ${error.message}`);
        }
        return;
      }

      setHasAcceptedTerms(true);
      setShowTermsModal(false);
      toast.success('Terms accepted');

      if (pendingAction === 'addFunds') {
        setShowAddFundsModal(true);
      } else if (pendingAction === 'addCard') {
        setShowAddCardModal(true);
      }
      setPendingAction(null);
    } catch (error) {
      console.error('Error accepting terms:', error);
      toast.error('An error occurred. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Reserve Dashboard | Defera</title>
      </Helmet>

      <div className="min-h-screen bg-[#050505] text-white relative overflow-hidden">
        {/* Ambient background */}
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-emerald-500/[0.03] rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-0 w-[400px] h-[400px] bg-white/[0.02] rounded-full blur-3xl" />
        </div>

        <div className="relative pt-12 pb-16 px-6 md:px-8 lg:px-10 max-w-5xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="mb-12"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h1 className="text-3xl md:text-4xl font-light tracking-tight text-white">Reserve</h1>
                <p className="text-white/50 font-normal text-sm mt-1 tracking-wide">Your travel fund</p>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowAllTripsModal(true)}
                      className="text-white/50 hover:text-white hover:bg-white/5 rounded-full border border-white/10 transition-colors"
                    >
                      All Trips
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowLedgerRepairModal(true)}
                      className="text-white/50 hover:text-white hover:bg-white/5 rounded-full border border-white/10 transition-colors"
                    >
                      Ledger sync
                    </Button>
                  </>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRefreshAll}
                  className="text-white/50 hover:text-white hover:bg-white/5 rounded-full border border-white/10 transition-colors"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
            {actAsUserId ? (
              <div className="mt-4 flex items-start gap-3 rounded-xl bg-emerald-500/[0.07] border border-emerald-500/25 px-3.5 py-3">
                <div className="mt-0.5 flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/15 ring-1 ring-inset ring-emerald-500/30">
                  <Eye className="w-3 h-3 text-emerald-300" strokeWidth={2.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-emerald-50 leading-tight">
                    Viewing as another user
                  </p>
                  <p className="mt-0.5 text-[12px] sm:text-[13px] text-emerald-200/75 leading-snug">
                    Showing Reserve data for <span className="font-mono text-[11px] opacity-90">{actAsUserId}</span>
                  </p>
                </div>
              </div>
            ) : null}
          </motion.div>

          {/* Main grid */}
          <div className="grid lg:grid-cols-2 gap-6 mb-10">
            {/* Balance Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <ReserveBalanceCard
                balanceCents={balance.balance_cents}
                totalAllocatedCents={reserveTrips.length > 0
                  ? reserveTrips.filter((t) => t.status === 'active').reduce((s, t) => s + (Number(t?.allocated_from_reserve_cents ?? t?.allocatedFromReserveCents) || 0), 0)
                  : (Number(balance.total_allocated_cents) || 0)}
                updatedAt={balance.updated_at}
                isLoading={isLoadingBalance}
                onAddFunds={handleAddFundsClick}
              />
            </motion.div>

            {/* Payment Methods */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <PaymentMethodsManager
                paymentMethods={paymentMethods}
                defaultPaymentMethod={defaultPaymentMethod}
                isLoading={isLoadingPaymentMethods}
                stripePaymentsAvailable={stripePaymentsAvailable}
                paymentMethodsError={paymentMethodsError}
                stripeLoadError={stripeLoadError}
                onRefresh={fetchDashboard}
                onAddCard={handleAddCardClick}
                onSetDefault={handleSetDefaultCard}
                onRemoveCard={handleRemoveCard}
              />
            </motion.div>
          </div>

          {/* Reserve Trips */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="mb-10"
          >
            <ReserveTripsCard
              trips={reserveTrips}
              balanceCents={balance.balance_cents}
              totalAllocatedCents={reserveTrips.length > 0
                ? reserveTrips.filter((t) => t.status === 'active').reduce((s, t) => s + (Number(t?.allocated_from_reserve_cents ?? t?.allocatedFromReserveCents) || 0), 0)
                : (Number(balance.total_allocated_cents) || 0)}
              allocationColumnMissing={balance.allocation_column_missing}
              isLoading={isLoadingTrips}
              onRefresh={async () => {
                await Promise.all([fetchTrips(), fetchBalance(), fetchLedger()]);
              }}
            />
          </motion.div>

          {/* Activity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          >
            <ReserveLedgerTable
              entries={ledgerEntries}
              isLoading={isLoadingLedger}
            />
          </motion.div>

          {/* FAQ */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12"
          >
            <ReserveFAQ />
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="mt-8 pt-12 border-t border-white/5"
          >
            {/* Legal Disclosure */}
            <div className="mb-8">
              <p className="text-white/30 text-[11px] leading-relaxed">
                Defera Reserve is a closed-loop stored value program. Credits purchased through Reserve are non-refundable, non-transferable, and may not be redeemed for cash or cash equivalents. Reserve is not a bank account, deposit account, demand account, savings account, or any other type of account subject to FDIC or NCUA insurance. Credits do not accrue interest and have no cash value. Credits may only be applied toward eligible travel services offered through the Defera platform. All purchases are subject to Defera's Terms of Service. Pricing for travel services remains dynamic until the point of ticketing; Reserve does not guarantee, lock, or hold any fare, rate, or availability. Defera reserves the right to modify or discontinue Reserve at any time. For complete terms, see <Link to="/reserve/terms" className="underline hover:text-white/50 transition-colors">Defera Reserve Terms & Conditions</Link>.
              </p>
            </div>

            {/* Footer Links */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-[11px] text-white/25">
              <p>© {new Date().getFullYear()} Defera. All rights reserved.</p>
              <div className="flex items-center gap-6">
                <Link to="/reserve/terms" className="hover:text-white/40 transition-colors">Reserve Terms</Link>
                <Link to="/terms" className="hover:text-white/40 transition-colors">Terms of Service</Link>
                <Link to="/privacy" className="hover:text-white/40 transition-colors">Privacy Policy</Link>
                <Link to="/help" className="hover:text-white/40 transition-colors">Help Center</Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Add Funds Modal */}
      <AddFundsModal
        isOpen={showAddFundsModal}
        onClose={() => {
          setShowAddFundsModal(false);
          // Clear the queued suggestion so reopening Add Funds normally goes back to
          // the generic flow (no pre-fill, no return-to navigation).
          setTopupSuggestion(null);
        }}
        paymentMethods={paymentMethods}
        defaultPaymentMethod={defaultPaymentMethod}
        onSuccess={handlePaymentSuccess}
        onAddCard={() => {
          setShowAddFundsModal(false);
          setShowAddCardModal(true);
        }}
        stripePromise={stripePromise}
        suggestedAmountCents={topupSuggestion?.amountCents}
        suggestedAmountLabel={topupSuggestion?.label || undefined}
        onSuccessReturnTo={topupSuggestion?.returnTo || undefined}
      />

      {/* Add Card Modal */}
      <AddCardModal
        isOpen={showAddCardModal}
        onClose={() => setShowAddCardModal(false)}
        onSuccess={handleCardAdded}
        stripePromise={stripePromise}
      />

      {/* Terms Modal */}
      <ReserveTermsModal
        isOpen={showTermsModal}
        onClose={() => {
          setShowTermsModal(false);
          setPendingAction(null);
        }}
        onAccept={handleTermsAccepted}
      />

      {/* Admin: All Reserve Trips (admin-only) */}
      <AdminAllTripsModal open={showAllTripsModal} onClose={() => setShowAllTripsModal(false)} />
      <ReserveLedgerRepairModal
        open={showLedgerRepairModal}
        onClose={() => setShowLedgerRepairModal(false)}
        onSuccess={fetchDashboard}
        actAsUserId={actAsUserId || ''}
      />
    </>
  );
}
